import {Button, ButtonProps, Icon} from "@blueprintjs/core";
import styled from "@emotion/styled";
import React from "react";

import {black, grayDark, grayLight, greenDark, redMiddle} from "../../../const/color";

const buttonStyles = {
    default: {
        border: `1px solid ${grayDark} !important`,
        backgroundColor: "white !important",
    },
    primary: {
        backgroundColor: `${greenDark} !important`,
        color: "white !important",
    },
    secondary: {
        backgroundColor: `${grayDark} !important`,
        color: "white !important",
    },
    inactive: {
        backgroundColor: `${grayLight} !important`,
        color: "white !important",
    },
    caution: {
        backgroundColor: `${redMiddle} !important`,
        color: "white !important",
    },
} as const;

type VariantType = keyof typeof buttonStyles;

type BaseButtonProps = ButtonProps & {
    width?: number;
    tone?: VariantType;
};

export const CustomButton = styled(Button)<BaseButtonProps>(({width, tone = "default"}) => ({
    "alignSelf": "flex-start",
    "padding": "8px 12px",
    "transition": "all 0.3s",
    "whiteSpace": "nowrap",
    ...(width && {minWidth: `${width}px`}),
    ...buttonStyles[tone],

    "&:hover": {
        filter: "brightness(0.9)",
    },
    ".bp3-icon": {
        color: "inherit",
    },

    "&:disabled": {
        "cursor": "not-allowed",
        "backgroundColor": `${grayDark} !important`,
        "opacity": "0.4",

        "&:hover": {
            opacity: " 0.4",
        },
    },
}));

export const IconButton = styled.button`
    align-self: flex-start;
    background-color: transparent;
    border: none;
    cursor: pointer;
    color: ${black};
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;

        &:hover {
            opacity: 0.4;
        }
    }
`;

type ActionButtonProps = Omit<BaseButtonProps, "icon" | "type" | "aria-label" | "fill"> & {
    isIcon?: boolean;
    isFill?: boolean;
    children?: React.ReactNode;
};

export const AddButton: React.FC<ActionButtonProps> = ({
    width = 100,
    children,
    isFill = false,
    ...rest
}) => (
    <CustomButton
        icon="add"
        type="button"
        aria-label={`${typeof children === "string" ? children : ""}`}
        width={width}
        fill={isFill ? true : false}
        minimal={true}
        {...rest}
    >
        {children}
    </CustomButton>
);

export const EditButton: React.FC<ActionButtonProps> = ({
    width = 100,
    children,
    isFill = false,
    ...rest
}) => (
    <CustomButton
        icon="edit"
        type="button"
        aria-label="編集"
        width={width}
        minimal={true}
        fill={isFill ? true : false}
        {...rest}
    >
        {children}
    </CustomButton>
);

export const DeleteButton: React.FC<ActionButtonProps> = ({
    width = 100,
    children,
    isIcon = true,
    isFill = false,
    ...rest
}) => (
    <CustomButton
        icon={isIcon ? "trash" : undefined}
        type="button"
        aria-label="削除実行"
        tone="caution"
        width={width}
        minimal={true}
        fill={isFill ? true : false}
        {...rest}
    >
        {children}
    </CustomButton>
);

export const SaveButton: React.FC<ActionButtonProps> = ({
    width = 100,
    children,
    isFill = false,
    ...rest
}) => (
    <CustomButton
        icon="saved"
        type="submit"
        tone="primary"
        aria-label="保存"
        width={width}
        minimal={true}
        fill={isFill ? true : false}
        {...rest}
    >
        {children}
    </CustomButton>
);

export const CancelButton: React.FC<ActionButtonProps> = ({
    width = 100,
    children,
    isFill = false,
    ...rest
}) => (
    <CustomButton
        type="button"
        tone="secondary"
        aria-label="キャンセル"
        width={width}
        minimal={true}
        fill={isFill ? true : false}
        {...rest}
    >
        {children}
    </CustomButton>
);

export const TrashIconButton: React.FC<ActionButtonProps> = (props) => (
    <IconButton type="button" aria-label="削除" {...props}>
        <Icon icon="trash" />
    </IconButton>
);

export const EditIconButton: React.FC<ActionButtonProps> = (props) => (
    <IconButton type="button" aria-label="編集" {...props}>
        <Icon icon="edit" />
    </IconButton>
);

export const RefreshButton: React.FC<ButtonProps> = ({children, ...rest}) => (
    <Button icon="refresh" {...rest}>
        {children}
    </Button>
);

export const NarrowDownButton: React.FC<ButtonProps> = ({children, ...rest}) => (
    <Button intent="primary" icon="search" {...rest}>
        {children}
    </Button>
);
type TabButtonProps = {
    isActive: boolean;
    onClick: () => void;
    label: string;
};
export const TabButton: React.FC<TabButtonProps> = ({isActive, onClick, label}) => (
    <CustomButton tone={isActive ? "primary" : "inactive"} onClick={onClick} minimal width={150}>
        {label}
    </CustomButton>
);

export const SettingIconButton: React.FC<ActionButtonProps> = (props) => (
    <IconButton type="button" aria-label="設定" {...props}>
        <Icon icon="settings" />
    </IconButton>
);
